/**
 * Apply a promotion code to a subscription (TMMPDM-0008).
 *
 * TMMPDM-0078 (tmm-api 0124): any 409 from Apply re-reads the subscription, whatever its code. The kind of
 * the code isn't known before the apply, and an `OFFER` apply that loses a race answers 409 after the
 * subscription has already moved to the code's offer. The dialog stays open with its words (TMM-B4) and
 * `onChanged` refreshes the detail underneath, so closing it shows the true offer and promotion.
 */
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, isValidationFailed, validationFields } from '@/api/errors';

interface PromotionDialogProps {
  open: boolean;
  slug: string;
  subscriptionId: number;
  onClose: () => void;
  onDone: () => void;
  /** A 409 from Apply: the row may have changed under the dialog, which stays open. */
  onChanged: () => void;
}

export function PromotionDialog({
  open,
  slug,
  subscriptionId,
  onClose,
  onDone,
  onChanged,
}: PromotionDialogProps) {
  const { t } = useCopy();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('subscriptions.promotion.applyTitle', 'Apply promotion')}</DialogTitle>
      {open ? (
        <PromotionForm
          slug={slug}
          subscriptionId={subscriptionId}
          onClose={onClose}
          onDone={onDone}
          onChanged={onChanged}
        />
      ) : null}
    </Dialog>
  );
}

function PromotionForm({
  slug,
  subscriptionId,
  onClose,
  onDone,
  onChanged,
}: Omit<PromotionDialogProps, 'open'>) {
  const { t } = useCopy();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      setFieldError(t('subscriptions.promotion.codeRequired', 'Code is required.'));
      return;
    }
    setSubmitting(true);
    setError(null);
    setFieldError(null);
    try {
      await adminApi.applyPromotion(slug, subscriptionId, { code: trimmed });
      onDone();
    } catch (err: unknown) {
      if (err instanceof ApiError && isValidationFailed(err)) {
        const fields = validationFields(err, t);
        setFieldError(fields.code ?? errorCopy(err, t, ''));
      } else {
        if (err instanceof ApiError && err.status === 409) onChanged();
        setError(
          errorCopy(err, t, t('subscriptions.promotion.error', "Couldn't apply the promotion.")),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label={t('subscriptions.promotion.code', 'Code')}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            error={Boolean(fieldError)}
            helperText={fieldError}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('subscriptions.subscribeDialog.cancel', 'Cancel')}</Button>
        <SubmitButton onClick={() => void submit()} busy={submitting}>
          {t('subscriptions.promotion.apply', 'Apply')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
